import type {SetIssueStatusMutationMutation} from '@github-ui/server-persisted-queries/types/setIssueStatusMutation'

type ConfirmedStatus = NonNullable<NonNullable<SetIssueStatusMutationMutation['setIssueStatus']>['issue']>['status']

export type IssueStatusOption = NonNullable<ConfirmedStatus>

type IssueStateData = {
  readonly state: string
  readonly stateReason?: string | null
}

/** A status whose category closes the issue as a duplicate needs a canonical issue to point at. */
export function needsDuplicateIssue(status: IssueStatusOption): boolean {
  return status.category.stateReason === 'DUPLICATE'
}

function fitsState(status: IssueStatusOption, issue: IssueStateData): boolean {
  if (status.category.state !== issue.state) return false
  return status.category.state === 'OPEN' || status.category.stateReason === (issue.stateReason ?? 'COMPLETED')
}

// The state can change without the status, like closing through the legacy close button, so a status
// only counts while it fits the state. Otherwise a closed issue falls back to the first status for its reason.
export function currentIssueStatus(
  issue: IssueStateData,
  statuses: readonly IssueStatusOption[],
  ...candidates: Array<IssueStatusOption | null | undefined>
): IssueStatusOption | undefined {
  const fitting = candidates.find(candidate => candidate && fitsState(candidate, issue))
  if (fitting) return fitting
  if (issue.state !== 'CLOSED') return undefined
  return statuses.find(status => fitsState(status, issue))
}

/**
 * The status the button offers next. Open statuses walk the owner's order, so the last of them
 * closes the issue. A closed status offers the first open status rather than the next closed one,
 * because swapping Done for Not planned is a poor primary action and reopening should land where
 * the server's own reopen lands. An owner with no open statuses keeps walking the order.
 */
export function nextIssueStatus(
  statuses: readonly IssueStatusOption[],
  current: IssueStatusOption | undefined,
): IssueStatusOption | undefined {
  if (current?.category.state === 'CLOSED') {
    const reopensTo = statuses.find(status => status.category.state === 'OPEN')
    if (reopensTo) return reopensTo
  }
  const index = current ? statuses.findIndex(status => status.id === current.id) : -1
  return statuses[(index + 1) % statuses.length]
}
